import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { PublicationsRepositoryService } from '../../services/publications-repository.service';
import { CommonModule } from '@angular/common';
import {
  PublicationSearchFilter,
  PublicationTagCheckbox,
  PublicationTagResponse,
} from '../../types/publications.types';
import {
  BehaviorSubject,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  take,
} from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-publications-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
})
export class TagsComponent implements OnInit, OnDestroy {
  tags$ = new BehaviorSubject<PublicationTagCheckbox[]>([]);

  private repository = inject(PublicationsRepositoryService);
  private ngUnsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.checkTags();
  }

  private checkTags(): void {
    this.repository.filters$
      .pipe(
        distinctUntilChanged((a, b) => this.areSameArrays(a, b)),
        combineLatestWith(this.repository.tags$),
        filter(([, tags]) => !!tags.length),
        map(
          ([{ publicationTagIds }, tags]: [
            PublicationSearchFilter,
            PublicationTagResponse[]
          ]) => this.tags$.next(this.convertToCheckbox(publicationTagIds, tags))
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private convertToCheckbox(
    selectedTags: string[] | null,
    tags: PublicationTagResponse[]
  ): PublicationTagCheckbox[] {
    return tags.map(
      (tag: PublicationTagResponse): PublicationTagCheckbox => ({
        ...tag,
        checked: selectedTags?.includes(String(tag.id)) ?? false,
      })
    );
  }

  toggleTag(id: number): void {
    this.repository.filters$
      .pipe(
        take(1),
        tap(({ publicationTagIds }): void => {
          const currentId = String(id);
          const selectedTags = new Set(publicationTagIds);

          if (selectedTags.has(currentId)) {
            selectedTags.delete(currentId);
          } else {
            selectedTags.add(currentId);
          }

          this.setQueryParams(
            selectedTags.size ? [...selectedTags].toString() : null
          );
        })
      )
      .subscribe();
  }

  cleanTags(): void {
    this.setQueryParams(null);
  }

  private setQueryParams(tags: string | null): void {
    this.repository.setQueryParamsUrl({ tags });
  }

  private areSameArrays(
    a: PublicationSearchFilter,
    b: PublicationSearchFilter
  ): boolean {
    return a.publicationTagIds?.toString() === b.publicationTagIds?.toString();
  }

  trackBy(index: number, tag: PublicationTagCheckbox): number {
    return tag.id;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
